import React from "react";
import PropTypes from "prop-types";
import { Dot, FileText, Check, Download, Trash2 } from "lucide-react";
import { LightTooltip } from "../ui/tooltip";
import { formatDateAndTimeFromISO } from "../../utils/dateHelper";
import { cn } from "../../lib/utils";

export const FileUpload = ({
  fileName,
  heading,
  subheading = null,
  isUpload = false,
}) => {
  return (
    <div className='w-100 w-md-50 flex-basis-1/2 text-center cursor-pointer'>
      <div className='flex flex-row gap-3'>
        <span className='bg-neutral-4 p-3 rounded-lg items-center flex'>
          <FileText className='w-4 h-4' color='#595959' />
        </span>
        <div className='flex flex-col text-left gap-1'>
          <span className='text-xs text-[var(--color-neutral-13)]'>{heading}</span>
          <span className='text-xs text-[var(--color-neutral-8)]'>{subheading}</span>
        </div>
        <input type="file" id="actual-btn" className="hidden"/>
        <label htmlFor="actual-btn" className="flex  items-center gap-2">  
        </label>
      </div>

    </div>
  );
};

export const DotSeprated = ({ left, right, className = "" }) => {
  return (
    <div className={`text-xs font-normal text-neutral-8 flex flex-row ${className}`}>
      {left}
      <Dot className='w-[15px] h-[15px]' />
      {right}
    </div>
  );
};

DotSeprated.propTypes = {
  left: PropTypes.node,
  right: PropTypes.node,
  className: PropTypes.string,
};

export const DocInfo = ({
  title,
  documents = [],
  handleDownload = () => {
    console.log("download");
  },
  handleDelete = () => {},
  handlePreview = () => {
    console.log("preview");
  },
  showDownload = true,
  showPreview = true,
  showDateTime = true,
  disableDelete = false,
  customClasses = {},
  icon: Icon = null,
}) => {
  const isClassifying = title === "Categorization In Progress";
  
  return (
    <div className={cn('space-y-4', customClasses?.docContainer)}>
      {title && (
        <div className={cn('font-semibold text-sm text-neutral-13 mb-3', isClassifying && 'opacity-50')}>{title}</div>
      )}
      {documents.map((singleDoc) => {
        const { createdOn, name } = singleDoc;
        const { date, time } = formatDateAndTimeFromISO(createdOn);
        return (
          <div
            key={singleDoc.documentId}
            className={cn('rounded bg-white', customClasses?.docRowContainer, isClassifying && 'opacity-50 pointer-events-none')}
          >
            <div className='flex items-center w-full'>
              {/* Check icon - fixed position at left */}
              <div className={cn('bg-system-positive-1 rounded-lg p-3 flex-shrink-0', customClasses?.docIconContainer)}>
                {Icon ? React.isValidElement(Icon) ? Icon : <Icon className='w-4 h-4 text-system-positive-2' /> : <Check className='w-4 h-4 text-system-positive-2' />}
              </div>

              {/* File info - takes remaining space in middle */}
              <div className='flex flex-col flex-1 min-w-0 mx-3'>
                <LightTooltip title={name} side='top' align='start'>
                  <div
                    className={cn(
                      'font-medium text-xs text-neutral-13 mb-1 truncate',
                      showPreview && !isClassifying && 'cursor-pointer'
                    )}
                    onClick={() => {
                      if(isClassifying) return;
                      if(showPreview) {
                        handlePreview(singleDoc);
                      }
                    }}
                  >
                    {name}
                  </div>
                </LightTooltip>
                {showDateTime && (
                  <div>
                    <DotSeprated left={date} right={time} />
                  </div>
                )}
              </div>

              {/* Action buttons - fixed position at right */}
              <div className='flex gap-2 flex-shrink-0'>
                {showDownload && (
                  <Download
                    className={cn(
                      'w-4 h-4',
                      isClassifying 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-secondary-2 cursor-pointer'
                    )}
                    onClick={() => {
                      if(isClassifying) return;
                      handleDownload(singleDoc);
                    }}
                  />
                )}
                <Trash2
                  className={cn(
                    'w-4 h-4',
                    isClassifying || disableDelete
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-system-negative-2 cursor-pointer'
                  )}
                  onClick={() => {
                    if(disableDelete || isClassifying) return;
                    handleDelete(singleDoc);
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const SingleDocInfo = ({
  document = {},
  handleDownload = () => {
    console.log("download");
  },
  handleDelete = () => {},
  handlePreview = () => {
    console.log("preview");
  },
  showDownload = true,
  showPreview = true,
  disableDelete = false
}) => {
    const { createdOn, name } = document;
        const { date, time } = formatDateAndTimeFromISO(createdOn);
        return (
          <div
            key={document.documentId}
            className='rounded bg-white'
          >
            <div className='flex items-center w-full'>
              {/* Check icon - fixed position at left */}
              <div className='bg-system-positive-1 rounded-lg p-3 flex-shrink-0'>
                <Check className='w-4 h-4 text-system-positive-2' />
              </div>

              {/* File info - takes remaining space in middle */}
              <div className='flex flex-col flex-1 min-w-0 mx-3'>
                <LightTooltip title={name} side='top' align='start'>
                  <div
                    className={`font-medium text-xs text-neutral-13 mb-1 truncate ${showPreview ? 'cursor-pointer' : ''}`}
                    onClick={showPreview ? () => {
                      handlePreview(document);
                    } : undefined}
                  >
                    {name}
                  </div>
                </LightTooltip>
                <div>
                  <DotSeprated left={date} right={time} />
                </div>
              </div>

              {/* Action buttons - fixed position at right */}
              <div className='flex gap-2 flex-shrink-0'>
                {showDownload && (
                  <Download
                    className='w-4 h-4 text-secondary-2 cursor-pointer'
                    onClick={() => {
                      handleDownload(document);
                    }}
                  />
                )}
                <Trash2
                  className={cn('w-4 h-4', !disableDelete && 'text-system-negative-2 cursor-pointer')}
                  onClick={() => {
                    if(disableDelete) return;
                    handleDelete(document);
                  }}
                />
              </div>
            </div>
          </div>

  );
};